import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import AdminLayout from '../../components/feature/AdminLayout';
import AteliersTable from './components/AteliersTable';
import CreateTenantModal, { type CreatedTenantResult } from './components/CreateTenantModal';
import StatusBadge from '../../components/base/StatusBadge';
import CredentialsPopup from '../order-plans/base/CredentialsPopup';
import {
  fetchTenant,
  suspendTenant,
  activateTenant,
  migrateTenant,
  seedTenant,
  deleteTenant,
  updateTenant,
} from '../../api/tenants.api';
import { fetchActivePlans } from '../../api/plans.api';
import type { AdminPlan } from '../../types/plan.types';
import { isTenantActive, tenantRowStatus, formatTenantDate } from '../../lib/tenant.utils';
import LoginAsTenantButton from '../../components/feature/LoginAsTenantButton';
import type { Tenant } from '../../types/tenant.types';
import CustomPlanFields, {
  CUSTOM_PLAN_VALUE,
  customPlanFormToPayload,
  emptyCustomPlanForm,
  todayDateInput,
  type CustomPlanFormState,
} from './components/CustomPlanFields';

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return `${p[0][0] ?? ''}${p[1][0] ?? ''}`.toUpperCase();
  return name.slice(0, 2).toUpperCase() || '—';
}

export default function AteliersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [detail, setDetail] = useState<Tenant | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTick, setDetailTick] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [customPlan, setCustomPlan] = useState<CustomPlanFormState>(emptyCustomPlanForm());
  const [planSaving, setPlanSaving] = useState(false);
  const [toolsError, setToolsError] = useState('');
  const [createdTenantCredentials, setCreatedTenantCredentials] = useState<{
    isOpen: boolean;
    email: string;
    password: string;
    tenantName?: string;
    loginUrl?: string;
    nextLinks?: {
      atelierPath?: string;
      subscriptionPath?: string;
      paymentsPath?: string;
    };
  }>({
    isOpen: false,
    email: '',
    password: '',
    tenantName: '',
    loginUrl: '',
  });
  const detailFetchIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailLoading(false);
      detailFetchIdRef.current = null;
      return;
    }
    const switchedTenant = detailFetchIdRef.current !== selectedId;
    detailFetchIdRef.current = selectedId;
    if (switchedTenant) {
      setDetail(null);
    }
    let cancelled = false;
    setDetailLoading(true);
    fetchTenant(selectedId).then((r) => {
      if (cancelled) return;
      setDetailLoading(false);
      if (r.ok) setDetail(r.tenant);
      else setDetail(null);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId, detailTick]);

  useEffect(() => {
    if (!selectedId) return;
    void fetchActivePlans().then((result) => {
      if (result.ok) setPlans(result.plans);
    });
  }, [selectedId]);

  useEffect(() => {
    if (detail?.is_custom_plan || detail?.plan?.slug === 'custom') {
      setSelectedPlanId(CUSTOM_PLAN_VALUE);
      const custom = detail.custom_subscription;
      if (custom) {
        setCustomPlan({
          monthly_price: custom.monthly_price ?? '',
          yearly_price: custom.yearly_price ?? '',
          billing_interval: custom.billing_interval === 'yearly' ? 'yearly' : 'monthly',
          starts_at: (custom.starts_at ?? todayDateInput()).slice(0, 10),
          currency: custom.currency ?? 'EGP',
          features: custom.features ?? {},
        });
      }
    } else if (detail?.plan_id) {
      setSelectedPlanId(String(detail.plan_id));
    } else if (detail?.plan?.id) {
      setSelectedPlanId(String(detail.plan.id));
    }
  }, [detail?.plan_id, detail?.plan?.id, detail?.is_custom_plan, detail?.custom_subscription]);

  useEffect(() => {
    if (!selectedId) setDeleteConfirmOpen(false);
  }, [selectedId]);

  const bumpList = () => setListVersion((v) => v + 1);

  const handleSavePlan = async () => {
    if (!detail?.id) return;
    const isCustom = selectedPlanId === CUSTOM_PLAN_VALUE;
    if (!isCustom && !selectedPlanId) return;
    setPlanSaving(true);
    setToolsError('');
    const result = await updateTenant(
      detail.id,
      isCustom
        ? {
            name: detail.name,
            custom_plan: true,
            custom_subscription: customPlanFormToPayload(customPlan),
          }
        : {
            name: detail.name,
            plan_id: Number(selectedPlanId),
          },
    );
    setPlanSaving(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setToolsError(result.message);
      return;
    }
    bumpList();
    setDetailTick((x) => x + 1);
  };

  const handleToggleFromDetail = async () => {
    if (!selected?.id) return;
    setToggleBusy(true);
    const active = isTenantActive(selected);
    const result = active
      ? await suspendTenant(selected.id)
      : await activateTenant(selected.id);
    setToggleBusy(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      return;
    }
    bumpList();
    setDetailTick((x) => x + 1);
  };

  const handleMigrate = async () => {
    if (!selected?.id) return;
    setToolsError('');
    setMigrateBusy(true);
    const result = await migrateTenant(selected.id);
    setMigrateBusy(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setToolsError(result.message);
      return;
    }
    bumpList();
    setDetailTick((x) => x + 1);
  };

  const handleSeed = async () => {
    if (!selected?.id) return;
    setToolsError('');
    setSeedBusy(true);
    const result = await seedTenant(selected.id);
    setSeedBusy(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setToolsError(result.message);
      return;
    }
    bumpList();
    setDetailTick((x) => x + 1);
  };

  const handleDeleteTenantConfirm = async () => {
    if (!selected?.id) return;
    setToolsError('');
    setDeleteBusy(true);
    const result = await deleteTenant(selected.id);
    setDeleteBusy(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setToolsError(result.message);
      setDeleteConfirmOpen(false);
      return;
    }
    setDeleteConfirmOpen(false);
    setSelectedId(null);
    bumpList();
  };

  const selected = detail;

  return (
    <AdminLayout>
      <AteliersTable
        listVersion={listVersion}
        onAdd={() => setCreateOpen(true)}
        onViewDetails={(row) => setSelectedId(row.id)}
        onTenantMutated={() => setDetailTick((x) => x + 1)}
      />

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(result: CreatedTenantResult) => {
          bumpList();
          setCreatedTenantCredentials({
            isOpen: true,
            email: result.email,
            password: result.password,
            tenantName: result.tenantName,
            loginUrl: result.loginUrl,
            nextLinks: {
              atelierPath: result.tenantId ? `/ateliers?highlight=${result.tenantId}` : '/ateliers',
              subscriptionPath: '/subscriptions',
              paymentsPath: '/payments',
            },
          });
        }}
      />

      <CredentialsPopup
        isOpen={createdTenantCredentials.isOpen}
        onClose={() => setCreatedTenantCredentials((prev) => ({ ...prev, isOpen: false }))}
        email={createdTenantCredentials.email}
        password={createdTenantCredentials.password}
        tenantName={createdTenantCredentials.tenantName}
        loginUrl={createdTenantCredentials.loginUrl}
        nextLinks={createdTenantCredentials.nextLinks}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={t('ateliers.modal.delete_confirm_title')}
        description={t('ateliers.modal.delete_tenant_confirm', {
          name: selected?.name ?? '—',
        })}
        confirmLabel={t('ateliers.modal.delete_confirm_action')}
        cancelLabel={t('ateliers.create.cancel')}
        loading={deleteBusy}
        onClose={() => !deleteBusy && setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteTenantConfirm}
        variant="danger"
      />

      {selectedId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-gray-900">{t('ateliers.modal.title')}</h3>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {detailLoading || !selected ? (
              <div className="p-12 flex flex-col items-center justify-center gap-2 text-gray-400">
                <i className="ri-loader-4-line animate-spin text-2xl" />
                <span className="text-sm">{t('ateliers.loading')}</span>
              </div>
            ) : (
              <>
                <div className="p-6 space-y-5">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-teal-100 text-teal-700 text-xl font-bold flex items-center justify-center flex-shrink-0">
                      {initials(selected.name)}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{selected.name}</h4>
                      <p className="text-sm text-gray-500">{selected.email ?? selected.slug ?? ''}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <StatusBadge status={tenantRowStatus(selected)} size="sm" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        label: t('ateliers.modal.tenant_id'),
                        value: selected.id,
                        icon: 'ri-fingerprint-line',
                      },
                      {
                        label: t('ateliers.modal.phone'),
                        value: selected.phone?.trim() || '—',
                        icon: 'ri-phone-line',
                      },
                      {
                        label: t('ateliers.modal.created'),
                        value: formatTenantDate(selected.created_at),
                        icon: 'ri-calendar-line',
                      },
                      {
                        label: t('ateliers.modal.created_by'),
                        value:
                          selected.created_by?.name ||
                          selected.created_by?.email ||
                          '—',
                        icon: 'ri-user-star-line',
                      },
                      {
                        label: t('ateliers.modal.updated'),
                        value: formatTenantDate(selected.updated_at),
                        icon: 'ri-time-line',
                      },
                    ].map((item) => (
                      <div key={item.label} className="bg-gray-50 rounded-xl p-3 sm:col-span-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className={`${item.icon} text-xs text-gray-400`} />
                          <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 break-all">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-4 space-y-3">
                    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">
                      {t('ateliers.modal.login_as')}
                    </p>
                    <p className="text-xs text-teal-600/80">{t('ateliers.modal.login_url_hint')}</p>
                    <LoginAsTenantButton tenantId={selected.id} variant="button" />
                  </div>

                  <div className="rounded-xl border border-gray-100 p-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t('ateliers.modal.plan')}
                    </p>
                    <p className="text-sm font-semibold text-gray-800">
                      {selected.is_custom_plan || selected.plan?.slug === 'custom'
                        ? t('ateliers.custom_plan.option')
                        : (selected.plan?.name ?? '—')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={selectedPlanId}
                        onChange={(e) => setSelectedPlanId(e.target.value)}
                        disabled={planSaving}
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
                      >
                        {plans.map((plan) => (
                          <option key={plan.id} value={String(plan.id)}>
                            {plan.title || plan.name}
                          </option>
                        ))}
                        <option value={CUSTOM_PLAN_VALUE}>{t('ateliers.custom_plan.option')}</option>
                      </select>
                      <button
                        type="button"
                        disabled={planSaving || !selectedPlanId}
                        onClick={() => void handleSavePlan()}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {planSaving ? t('ateliers.edit.submitting') : t('ateliers.modal.save_plan')}
                      </button>
                    </div>
                    {selectedPlanId === CUSTOM_PLAN_VALUE ? (
                      <CustomPlanFields value={customPlan} onChange={setCustomPlan} disabled={planSaving} />
                    ) : null}
                  </div>
                </div>

                {toolsError && (
                  <div className="px-6 pb-2">
                    <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                      {toolsError}
                    </p>
                  </div>
                )}

                <div className="px-6 pb-3 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    disabled={migrateBusy || seedBusy || deleteBusy}
                    onClick={() => void handleMigrate()}
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    {migrateBusy ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-database-2-line" />
                    )}
                    {t('ateliers.modal.migrate')}
                  </button>
                  <button
                    type="button"
                    disabled={migrateBusy || seedBusy || deleteBusy}
                    onClick={() => void handleSeed()}
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    {seedBusy ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-seedling-line" />
                    )}
                    {t('ateliers.modal.seed')}
                  </button>
                  <button
                    type="button"
                    disabled={migrateBusy || seedBusy || deleteBusy}
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line" />
                    {t('ateliers.modal.delete_tenant')}
                  </button>
                </div>

                <div className="px-6 pb-5 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    disabled={toggleBusy || migrateBusy || seedBusy || deleteBusy}
                    onClick={() => void handleToggleFromDetail()}
                    className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors ${
                      isTenantActive(selected)
                        ? 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
                        : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {toggleBusy ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className={isTenantActive(selected) ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                    )}
                    {isTenantActive(selected) ? t('actions.suspend') : t('actions.activate')}
                  </button>
                  <button
                    type="button"
                    disabled={migrateBusy || seedBusy || deleteBusy}
                    onClick={() => setSelectedId(null)}
                    className="px-4 py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
